// ================================
// TrainingPage.jsx (revised - 2 steps only)
// ================================
import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import RichChatEditor from "./RichChatEditor";
import "../modules/Train.css";
import { useT, useI18nReady } from "../components/TextProvider";
import {
  getCurrentUser,
  getStudentProgress,
  initializeProgress,
  updateTartib,
  updateMashqTartib,
} from "../utils/progressService";

// true bo'lsa AI chaqirilmaydi va qadam avtomatik yakunlanadi (faqat test uchun)
const BYPASS_AI = false;

const API_BASE = "https://apitr.subxonqulov.uz/api/vazifalar";
const LOG_API = "https://apitr.subxonqulov.uz/api/vazifa-users/";
const COMMENTS_API = "https://apitr.subxonqulov.uz/api/comments/";

function uid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function Pill({ children }) {
  return <span className="pill">{children}</span>;
}

function Section({ title, right, children }) {
  return (
    <section className="section">
      <div className="section-header">
        <h2>{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
}

function ChatBubble({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`chat-bubble ${isUser ? "user" : "assistant"}`}>
      {msg.pending ? (
        <div className="typing">
          <span className="dot" />
          <span className="dot" />
          <span className="dot" />
        </div>
      ) : (
        <>
          {msg.text && (
            <div
              className="ai-richtext"
              dangerouslySetInnerHTML={{ __html: formatAiTextToHTML(msg.text) }}
            />
          )}
          {msg.imageUrl && <img src={msg.imageUrl} alt="uploaded" />}
        </>
      )}
      <div className="time">{new Date(msg.ts).toLocaleTimeString()}</div>
    </div>
  );
}

function ChatView({ messages }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [messages]);
  return (
    <div ref={ref} className="chat-view">
      {messages.map((m) => (
        <ChatBubble key={m.id} msg={m} />
      ))}
    </div>
  );
}

function Stars({ value, onChange }) {
  return (
    <div className="stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          onClick={() => onChange(n)}
          className={n <= value ? "active" : ""}
        >
          ★
        </button>
      ))}
    </div>
  );
}

// --- helpers ---
function replaceMsgById(list, id, updater) {
  return list.map((m) => (m.id === id ? updater(m) : m));
}

function buildAuthHeaders() {
  const headers = { "Content-Type": "application/json" };
  try {
    let token = null;
    token =
      localStorage.getItem("auth:token") ||
      localStorage.getItem("auth:access") ||
      localStorage.getItem("auth:key") ||
      localStorage.getItem("auth:jwt") ||
      localStorage.getItem("auth:auth_token");

    if (token) {
      const isJwt = token.startsWith("eyJ");
      headers["Authorization"] = isJwt ? `Bearer ${token}` : `Token ${token}`;
      console.log("Auth header set with", isJwt ? "Bearer" : "Token", "scheme");
    } else {
      console.warn("No auth token found in localStorage");
    }
  } catch (e) {
    console.warn("Error building auth headers:", e);
  }
  return headers;
}

function stripHTML(html = "") {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

function formatAiTextToHTML(text = "") {
  if (!text) return "";

  // Avval HTML teglarni escape qilish
  let safe = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // **boldi matn** → <strong>boldi matn</strong>
  safe = safe.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");

  // *italic matn* → <em>italic matn</em>
  safe = safe.replace(/\*(.+?)\*/g, "<em>$1</em>");

  // __underline__ → <u>underline</u>
  safe = safe.replace(/__(.+?)__/g, "<u>$1</u>");

  // ~~strikethrough~~ → <s>strikethrough</s>
  safe = safe.replace(/~~(.+?)~~/g, "<s>$1</s>");

  // ### Sarlavha 3 → <h3>Sarlavha 3</h3>
  safe = safe.replace(/^### (.+)$/gm, "<h3>$1</h3>");

  // ## Sarlavha 2 → <h4>Sarlavha 2</h4>
  safe = safe.replace(/^## (.+)$/gm, "<h4>$1</h4>");

  // # Sarlavha 1 → <h5>Sarlavha 1</h5>
  safe = safe.replace(/^# (.+)$/gm, "<h5>$1</h5>");

  // - Ro'yxat elementi → <li>Ro'yxat elementi</li>
  safe = safe.replace(/^[-•] (.+)$/gm, "<li>$1</li>");

  // <li> teglarni <ul> ga o'rash
  safe = safe.replace(/(<li>.+?<\/li>)/s, "<ul>$1</ul>");

  // 1. Raqamli ro'yxat → <li>Raqamli ro'yxat</li>
  safe = safe.replace(/^\d+\. (.+)$/gm, "<li>$1</li>");

  // Qo'sh yangi qator → paragraf
  safe = safe.replace(/\n\n+/g, "</p><p>");
  safe = `<p>${safe}</p>`;

  // Bitta yangi qator → br
  safe = safe.replace(/\n/g, "<br>");

  // Bo'sh paragraflarni o'chirish
  safe = safe.replace(/<p><\/p>/g, "");
  safe = safe.replace(/<p><br><\/p>/g, "");

  return safe;
}

async function getLogById(id) {
  try {
    const res = await fetch(`${LOG_API}${id}/`, { headers: buildAuthHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch (e) {
    console.warn("GET /vazifa-users/{id} failed:", e);
    return null;
  }
}

async function pollNatijaById(id, { intervalMs = 500, timeoutMs = 60000 } = {}) {
  const start = Date.now();
  let attempts = 0;
  let lastCheck = 0;
  
  while (Date.now() - start < timeoutMs) {
    attempts++;
    try {
      const item = await getLogById(id);
      const natija = item?.natija && stripHTML(item.natija);
      
      if (natija) {
        console.log(`✓ Got natija after ${Date.now() - start}ms (attempt ${attempts}):`, natija.substring(0, 100));
        return natija;
      } else {
        console.log(`  Polling attempt ${attempts}: natija still empty`);
      }
    } catch (e) {
      console.warn(`  Polling attempt ${attempts} failed:`, e.message);
    }
    
    const elapsed = Date.now() - lastCheck;
    lastCheck = Date.now();
    
    if (elapsed < intervalMs) {
      await new Promise((r) => setTimeout(r, intervalMs - elapsed));
    }
  }
  
  console.warn(`✗ Timeout waiting for natija after ${Date.now() - start}ms (${attempts} attempts)`);
  return null;
}

function AutoTextarea({
  value,
  onChange,
  placeholder = "",
  minRows = 1,
  maxRows = 6,
  className = "",
  onKeyDown,
}) {
  const ref = React.useRef(null);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const line = parseFloat(getComputedStyle(el).lineHeight) || 24;
    const minH = minRows * line;
    const maxH = maxRows * line;

    el.style.height = "auto";
    const sh = el.scrollHeight;

    if (sh <= maxH) {
      el.style.overflowY = "hidden";
      el.style.height = Math.max(sh, minH) + "px";
    } else {
      el.style.overflowY = "auto";
      el.style.height = maxH + "px";
    }

    el.style.transform = "scale(0.99)";
    requestAnimationFrame(() => (el.style.transform = "scale(1)"));
  }, [value, minRows, maxRows]);

  return (
    <textarea
      ref={ref}
      className={`auto-textarea ${className}`}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onKeyDown={onKeyDown}
      rows={minRows}
    />
  );
}

export default function TrainingPage() {
  const t = useT();
  const ready = useI18nReady?.();
  const { id: routeId } = useParams();
  const navigate = useNavigate();

  // --- UI state ---
  const [step, setStep] = useState(1);

  // step 1 chat
  const [messages1, setMessages1] = useState([]);
  const [aiAnswers, setAiAnswers] = useState([]);

  // step 2
  const [step2Response, setStep2Response] = useState("");
  const [step2Loading, setStep2Loading] = useState(false);
  const [step2Completed, setStep2Completed] = useState(false);
  const [canRetakeStep2, setCanRetakeStep2] = useState(false);
  const [step2AnswerCount, setStep2AnswerCount] = useState(0);
  const [tartibUpdated, setTartibUpdated] = useState(false);

  // task data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [questionHTML, setQuestionHTML] = useState("");
  const [miniLessonHTML, setMiniLessonHTML] = useState("");
  const [rejaHTML, setRejaHTML] = useState("");
  const [cardTitle, setCardTitle] = useState("");
  const [imgUrl, setImgUrl] = useState("");
  const [lessonTartib, setLessonTartib] = useState(null);
  const [fanId, setFanId] = useState(null);
  const [progressId, setProgressId] = useState(null);

  // --- Help modal state ---
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpLoading, setHelpLoading] = useState(false);
  const [helpError, setHelpError] = useState(null);
  const [helpHTML, setHelpHTML] = useState("");

  useEffect(() => {
    if (helpOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [helpOpen]);

  async function openHelpModal() {
    try {
      setHelpOpen(true);
      setHelpLoading(true);
      setHelpError(null);
      setHelpHTML("");

      const url = `https://apitr.subxonqulov.uz/api/tushunchalar/?vazifa=${encodeURIComponent(
        routeId
      )}`;
      const res = await fetch(url, { headers: buildAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      if (Array.isArray(data) && data.length) {
        const last = data[data.length - 1];
        setHelpHTML(last?.matni || "<p>(bo'sh)</p>");
      } else {
        setHelpHTML("<p>Bu vazifa uchun hali hech qanday material yo'q.</p>");
      }
    } catch (e) {
      setHelpError("Yuklab bo'lmadi.");
    } finally {
      setHelpLoading(false);
    }
  }

  function closeHelpModal() {
    setHelpOpen(false);
  }

  useEffect(() => {
    const controller = new AbortController();

    async function loadTask() {
      try {
        setLoading(true);
        setError(null);

        setStep(1);
        setMessages1([]);
        setAiAnswers([]);
        setStep2Response("");
        setStep2Loading(false);
        setProgressId(null);

        const url = `${API_BASE}/${routeId}/`;
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();

        setCardTitle(data.name || "");
        setImgUrl(data.img || "");
        setQuestionHTML(data.savol || "");
        setMiniLessonHTML(data.tushuncha_b2 || "");
        setRejaHTML(data.reja || "");
        setLessonTartib(data.tartib ?? null);
        setFanId(data.fan ?? null);

        console.log("Loaded task data:", { fan: data.fan, tartib: data.tartib });

        // ВАЖНО: инициализируем прогресс ДО всего остального
        // Используем getCurrentUser() чтобы гарантировать правильное получение ID
        if (data.fan) {
          const userId = getCurrentUser();
          console.log("Current user ID for initialization:", userId);
          
          if (userId) {
            const progress = await initializeProgress(data.fan, userId);
            if (progress?.id) {
              setProgressId(progress.id);
              console.log(
                "✓ Initialized/fetched progress:",
                progress.id,
                "tartib:",
                progress.tartib,
                "mashqtartib:",
                progress.mashqtartib,
                "test_attempts_count:",
                progress.test_attempts_count,
                "test_blocked:",
                progress.test_blocked
              );
            } else {
              console.warn("Failed to initialize progress for fan:", data.fan, "user:", userId);
            }
          } else {
            console.warn("No user ID available for progress initialization");
          }
        }
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error("Error loading task:", e);
          setError("Mashqni yuklab bo'lmadi.");
        }
      } finally {
        setLoading(false);
      }
    }

    if (routeId) {
      console.log("Loading task with routeId:", routeId);
      loadTask();
    }
    return () => controller.abort();
  }, [routeId]);

  useEffect(() => {
    const controller = new AbortController();

    async function loadHistory() {
      const userId = getCurrentUser();
      if (!userId || !routeId) return;
      try {
        const res = await fetch(
          `${LOG_API}?user=${encodeURIComponent(userId)}`,
          {
            signal: controller.signal,
            headers: buildAuthHeaders(),
          }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const list = await res.json();

        // Проверяем step 1 (full=false)
        const step1Records = (Array.isArray(list) ? list : [])
          .filter(
            (r) => String(r.vazifa) === String(routeId) && r.full === false
          )
          .sort((a, b) => a.id - b.id);

        if (!step1Records.length) return;

        const historyMsgs = [];
        const answers = [];
        for (const item of step1Records) {
          const ts = new Date(item.created_at).getTime() || Date.now();
          
          // Prompt-dan faqat talabaning asl so'rovini ajratib olish
          // Format: "USER_QUESTION\n\nJAVOB FORMATLANISHI..."
          const fullPrompt = stripHTML(item.promt || "");
          const promptParts = fullPrompt.split("\n\nJAVOB FORMATLANISHI");
          const promtText = promptParts[0].trim();
          
          const natijaText = stripHTML(item.natija || "");
          historyMsgs.push({
            id: `h-u-${item.id}`,
            role: "user",
            text: promtText,
            ts,
          });
          historyMsgs.push({
            id: `h-a-${item.id}`,
            role: "assistant",
            text: natijaText,
            ts: ts + 1,
          });
          answers.push(natijaText);
        }

        setMessages1((prev) => {
          if (!prev.length) return historyMsgs;
          const map = new Map(prev.map((m) => [m.id, m]));
          for (const m of historyMsgs) if (!map.has(m.id)) map.set(m.id, m);
          return Array.from(map.values());
        });

        setAiAnswers(answers);

        // Проверяем step 2 (full=true) - если есть хотя бы одна запись, значит второй шаг пройден
        const step2Records = (Array.isArray(list) ? list : [])
          .filter(
            (r) => String(r.vazifa) === String(routeId) && r.full === true
          );

        if (step2Records.length > 0) {
          console.log("Step 2 already completed. Found", step2Records.length, "full=true records");
          const lastStep2 = step2Records
            .filter((r) => r.natija)
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
          
          if (lastStep2) {
            const step2ResponseText = stripHTML(lastStep2.natija || "");
            
            // 🔑 НОВАЯ ЛОГИКА: Проверяем, может ли студент повторно получить ответ
            // x = количество step2 ответов (full=true с натиджей)
            const x = step2Records.filter((r) => r.natija).length;
            
            console.log("=== STEP 2 RETAKE CHECK ===");
            console.log("x (количество step2 ответов):", x);
            
            // y = test_attempts_count из /api/xolat/
            try {
              // 🔑 Используем fanId из состояния компонента, которое было загружено из данных задания
              // Это правильный ID предмета, а не ID задания (routeId)
              let y = null;
              
              console.log("fanId from state:", fanId);
              console.log("userId:", userId);
              console.log("routeId:", routeId);
              
              if (fanId && userId) {
                const progressUrl = `https://apitr.subxonqulov.uz/api/xolat/?fan=${fanId}&user=${userId}`;
                console.log("Fetching progress from URL:", progressUrl);
                
                const progressRes = await fetch(
                  progressUrl,
                  { headers: buildAuthHeaders() }
                );
                
                console.log("Progress response status:", progressRes.status);
                
                if (progressRes.ok) {
                  const progressData = await progressRes.json();
                  console.log("progressData (full response):", progressData);
                  
                  const progress = Array.isArray(progressData) && progressData.length > 0 
                    ? progressData[0] 
                    : null;
                  y = progress?.test_attempts_count ?? null;
                  
                  console.log("progress (first item):", progress);
                  console.log("y (test_attempts_count):", y);
                } else {
                  console.warn("Progress API returned status:", progressRes.status);
                  const errorText = await progressRes.text();
                  console.warn("Error response:", errorText.substring(0, 200));
                }
              }
              
              console.log("y (test_attempts_count из xolat):", y);
              console.log("x === y:", x === y);
              console.log("============================");
              
              // Сохраняем количество ответов
              setStep2AnswerCount(x);
              
              // Если x === y, студент может повторно получить ответ
              if (x === y) {
                setCanRetakeStep2(true);
                // Если x !== 0, не показываем предыдущий ответ
                if (x !== 0) {
                  setStep2Completed(false);
                  setStep2Response("");
                  console.log("✅ Student can retake step 2 (x === y && x !== 0) - clearing previous answer");
                } else {
                  setStep2Completed(true);
                  setStep2Response(step2ResponseText);
                  console.log("✅ Student can retake step 2 (x === y && x === 0)");
                }
              } else {
                setCanRetakeStep2(false);
                setStep2Completed(true);
                setStep2Response(step2ResponseText);
              }
            } catch (err) {
              console.warn("Error checking retake eligibility:", err);
              setCanRetakeStep2(false);
              setStep2Completed(true);
              setStep2Response(step2ResponseText);
            }
          }
        }
      } catch (e) {
        console.warn("Yuklab bo'lmadi:", e);
      }
    }

    loadHistory();
    return () => controller.abort();
  }, [routeId, fanId]);

  async function postInteraction({
    promt,
    natija,
    vazifa,
    user,
    full = false,
  }) {
    try {
      const res = await fetch(LOG_API, {
        method: "POST",
        headers: buildAuthHeaders(),
        body: JSON.stringify({ promt, natija, vazifa, user, full }),
      });
      
      if (res.ok) {
        return await res.json().catch(() => null);
      }
      
      // Даже при ошибке пытаемся получить данные из ответа
      const contentType = res.headers.get("content-type");
      let responseData = null;
      
      if (contentType?.includes("application/json")) {
        try {
          responseData = await res.json();
          console.log("POST error response data:", responseData);
          // Если есть ID в ошибке, возвращаем его
          if (responseData?.id) {
            console.warn("POST /vazifa-users/ returned error status but has ID:", responseData.id);
            return responseData;
          }
        } catch (e) {
          console.warn("Could not parse error response as JSON:", e);
        }
      }
      
      const text = await res.text().catch(() => "");
      console.warn("POST /vazifa-users/ failed:", res.status, text);
      return null;
    } catch (err) {
      console.warn("POST /vazifa-users/ error:", err);
      return null;
    }
  }

  const onSendUser1 = ({ text, file }) => {
    const userMsg = {
      id: uid(),
      role: "user",
      text: text || undefined,
      imageUrl: file ? URL.createObjectURL(file) : undefined,
      ts: Date.now(),
    };
    setMessages1((prev) => [...prev, userMsg]);

    const promtPayload = [text?.trim(), file ? "[image attached]" : ""]
      .filter(Boolean)
      .join(" ")
      .trim() || "(image)";

    (async () => {
      const placeholderId = uid();
      const now = Date.now();
      const userId = getCurrentUser();
      
      setMessages1((prev) => [
        ...prev,
        { id: placeholderId, role: "assistant", pending: true, ts: now },
      ]);

      const vazifaId = Number(routeId) || routeId;
      
      // Prompt-ga Markdown formatting talab qo'shish (talabaning so'rovida ko'rinmasin)
      const formatInstructions = `

JAVOB FORMATLANISHI (faqat AI uchun):
- Muhim atamalarni **bold** qilib belgilang
- Asosiy g'oyalarni *italic* qilib ta'kidlang
- Tushuntirishlarni paragraf ko'rinishida yozing
- Ro'yxatlar uchun - yoki 1. formatidan foydalaning
- Sarlavhalarni ### format bilan belgilang (agar kerak bo'lsa)`;

      const enhancedPrompt = promtPayload + formatInstructions;

      const created = await postInteraction({
        promt: enhancedPrompt,
        natija: undefined,
        vazifa: vazifaId,
        user: userId,
        full: false,
      });

      const logId = created?.id;
      if (!logId) {
        setMessages1((prev) =>
          replaceMsgById(prev, placeholderId, (m) => ({
            ...m,
            pending: false,
            text: "Xatolik: javobni olish imkoni bo'lmadi.",
          }))
        );
        return;
      }

      const natija = await pollNatijaById(logId, {
        intervalMs: 500,
        timeoutMs: 45000,
      });
      if (natija) {
        setMessages1((prev) =>
          replaceMsgById(prev, placeholderId, (m) => ({
            ...m,
            pending: false,
            text: natija,
          }))
        );
        setAiAnswers((prev) => [...prev, natija]);
      } else {
        setMessages1((prev) =>
          replaceMsgById(prev, placeholderId, (m) => ({
            ...m,
            pending: false,
            text: "Javob kechikmoqda. Iltimos, qayta urinib ko'ring.",
          }))
        );
      }
    })();
  };

  const sendComprehensiveAnswer = () => {
    console.log("sendComprehensiveAnswer called", {
      step2Loading,
      progressId,
      fanId,
      lessonTartib,
      messages1Length: messages1.length,
    });

    if (step2Loading) {
      console.log("Already loading or completed, returning");
      return;
    }

    const suhbat = messages1
      .filter((m) => m.role === "user")
      .map((m) => m.text)
      .join("\n");

    console.log("suhbat length:", suhbat.length);
    console.log("rejaHTML length:", rejaHTML.length);

    if (!suhbat.trim() && !BYPASS_AI) {
      setStep2Response(
        "Suhbat mavjud emas. Iltimos, birinchi qadamda savolga javob bering."
      );
      return;
    }

    const prompt = `${rejaHTML}
Yuqoridagilar yuzasidan suhbat olib borildi.
${suhbat}
Suhbat davomida ko'zda tutilgan yo'nalishlardan qay birlari haqida suhbatlashilmadi yoki yuzaki suhbatlashildi. Ularni to'ldiruvchi axbotorlarni bering.

MUHIM: Javobni quyidagi Markdown formatida berishingiz kerak:
- Muhim tushunchalar va atamalarni **bold** qilib belgilang
- Asosiy g'oyalarni ### Sarlavha formatida ko'rsating
- Ro'yxatlar uchun - yoki 1. formatidan foydalaning
- Muhim fikrlarni *italic* qilib ta'kidlang
- Agar tushuntirish kerak bo'lsa, tushuntirishni paragraf ko'rinishida yozing

Javob strukturasi:
1. Qisqacha kirish
2. Asosiy tushunchalar (sarlavha bilan)
3. Batafsil tushuntirish (ro'yxatlar bilan)
4. Xulosa yoki qo'shimcha maslahatlar`;

    setStep2Loading(true);
    setStep2Response("");

    (async () => {
      const vazifaId = Number(routeId) || routeId;
      const userId = getCurrentUser();

      if (BYPASS_AI) {
        if (progressId && fanId && userId && lessonTartib !== null && !tartibUpdated) {
          try {
            const latestProgress = await getStudentProgress(fanId, userId);
            if (latestProgress) {
              const newTartib = Number(latestProgress.tartib ?? 0) + 1;
              const updated = await updateTartib(progressId, newTartib);
              if (updated) setTartibUpdated(true);
            }
          } catch (e) {
            console.warn("Bypass: tartib update error:", e);
          }
        }
        setStep2Completed(true);
        setStep2AnswerCount((prev) => prev + 1);
        setStep2Response("Dars tugallandi.");
        setStep2Loading(false);
        return;
      }

      const created = await postInteraction({
        promt: prompt,
        natija: undefined,
        vazifa: vazifaId,
        user: userId,
        full: true,
      });

      if (!created?.id) {
        setStep2Response("Xatolik: javobni olish imkoni bo'lmadi.");
        setStep2Loading(false);
        return;
      }

      // tartibni darhol yangilash — AI javobini kutmasdan
      if (progressId && fanId && userId && lessonTartib !== null && !tartibUpdated) {
        try {
          const latestProgress = await getStudentProgress(fanId, userId);
          if (latestProgress) {
            const newTartib = Number(latestProgress.tartib ?? 0) + 1;
            const updated = await updateTartib(progressId, newTartib);
            if (updated) {
              setTartibUpdated(true);
              console.log("✓ tartib updated to", updated.tartib);
            }
          }
        } catch (e) {
          console.warn("Error updating tartib:", e);
        }
      }

      setStep2Response("Javob kutilmoqda...");
      await new Promise((r) => setTimeout(r, 1000));

      let natija = await pollNatijaById(created.id, {
        intervalMs: 200,
        timeoutMs: 35000,
      });

      if (!natija) {
        setStep2Response("Javob kelmamoqda, qayta urinilmoqda...");
        await new Promise((r) => setTimeout(r, 1000));
        natija = await pollNatijaById(created.id, {
          intervalMs: 300,
          timeoutMs: 30000,
        });
      }

      if (natija) {
        setStep2Completed(true);
        setStep2AnswerCount((prev) => prev + 1);
        setCanRetakeStep2(false);

        // Typewriter effekti — matn asta-sekin chiqadi
        let i = 0;
        const chunkSize = 8;
        const reveal = () => {
          i += chunkSize;
          if (i >= natija.length) {
            setStep2Response(natija);
          } else {
            setStep2Response(natija.slice(0, i));
            requestAnimationFrame(reveal);
          }
        };
        setStep2Loading(false);
        requestAnimationFrame(reveal);
        return;
      } else {
        setStep2Response("Javob kechikmoqda. Iltimos, qayta urinib ko'ring.");
      }
      setStep2Loading(false);
    })();
  };

  return (
    <div className="training-page">
      <header>
        {/* <div>
          <h1>{cardTitle}</h1>
        </div> */}
      </header>

      <div className="steps-progress">
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
          <div className={`step-dot ${step >= 1 ? (step > 1 ? "done" : "active") : ""}`}>1</div>
          <span style={{ fontSize: 11, color: "var(--muted)", whiteSpace: "nowrap" }}>AI bilan suhbat</span>
        </div>
        <div className={`step-line ${step > 1 ? "filled" : ""}`} />
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
          <div className={`step-dot ${step === 2 ? "active" : ""}`}>2</div>
          <span style={{ fontSize: 11, color: "var(--muted)", whiteSpace: "nowrap" }}>Xulosa va tahlil</span>
        </div>
      </div>

      {loading && (
        <Section title="Yuklanmoqda">
          <div className="skeleton skeleton-box" />
          <div className="skeleton skeleton-chat" />
        </Section>
      )}

      {error && !loading && <div className="error">{error}</div>}

      {!loading && !error && (
        <>
          {step === 1 && (
            <>
              <Section
                title={t("savol")}
                // right={
                //   <button className="btn-ghost" onClick={openHelpModal}>
                //     ?
                //   </button>
                // }
              >
                {/* {questionHTML ? (
                  <div
                    className="richtext"
                    dangerouslySetInnerHTML={{ __html: questionHTML }}
                  />
                ) : (
                  <p>Savol mavjud emas.</p>
                )} */}
                <p>{cardTitle}</p>
              </Section>

              <Section title={t("promptYozing")} className="answerSection">
                {messages1.length === 0 && (
                  <div style={{
                    background: "var(--card, #1e1e2e)",
                    border: "1px solid var(--border, #333)",
                    borderRadius: 8,
                    padding: "12px 16px",
                    marginBottom: 12,
                    fontSize: 13,
                    color: "var(--muted)",
                    lineHeight: 1.6,
                  }}>
                    💡 <strong>Qanday ishlaydi?</strong> Quyidagi matn maydoniga savolingizni yozing va AI bilan suhbatlashing.
                    Mavzuni yaxshiroq tushunib olgach, <em>Davom ettirish</em> tugmasini bosing.
                  </div>
                )}
                <ChatView messages={messages1} />
                <RichChatEditor
                  onSend={(html, plainText) => {
                    onSendUser1({ text: plainText, file: null });
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    marginTop: 12,
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                  {messages1.filter(m => m.role === "user").length === 0 && (
                    <span style={{ fontSize: 12, color: "var(--muted)", marginRight: "auto" }}>
                      Davom etishdan oldin AI bilan kamida bir marta suhbatlashing
                    </span>
                  )}
                  <button
                    className="btn-ghost hoverwhite"
                    onClick={() => navigate(`/mappage/${fanId}`)}
                    title="Mavzular ro'yxatiga qaytish"
                  >
                    {t('mappage')}
                  </button>
                  <button onClick={() => setStep(2)}>
                    {t("davomEttirish")}
                  </button>
                </div>
              </Section>
            </>
          )}

          {step === 2 && (
            <>
              <Section title="Savol:">
                {/* {questionHTML ? (
                  <div
                    className="richtext"
                    dangerouslySetInnerHTML={{ __html: questionHTML }}
                  />
                ) : (
                  <p>Savol mavjud emas.</p>
                )} */}
                <p>{cardTitle}</p>
              </Section>

              <div style={{
                background: "var(--card, #1e1e2e)",
                border: "1px solid var(--border, #333)",
                borderRadius: 8,
                padding: "10px 16px",
                marginBottom: 12,
                fontSize: 13,
                color: "var(--muted)",
                lineHeight: 1.6,
              }}>
                💡 <strong>2-bosqich:</strong> AI 1-bosqichdagi suhbatingizni tahlil qiladi va
                siz ko'rmagan yoki kam muhokama qilgan jihatlarni tushuntiradi.
                <em> "Javob olish"</em> tugmasini bosing.
              </div>

              <Section title="Xulosa va qo'shimcha ma'lumot:">
                {step2Loading ? (
                  <div
                    style={{
                      color: "var(--muted)",
                      fontSize: 14,
                      textAlign: "center",
                      padding: "20px",
                    }}
                  >
                    <div
                      className="typing"
                      style={{ justifyContent: "center" }}
                    >
                      <span className="dot" />
                      <span className="dot" />
                      <span className="dot" />
                    </div>
                    <p>{t('answerComing')}</p>
                  </div>
                ) : step2Response ? (
                  <div
                    className="richtext"
                    dangerouslySetInnerHTML={{
                      __html: formatAiTextToHTML(step2Response),
                    }}
                  />
                ) : (
                  <div style={{ color: "var(--muted)", fontSize: 14, textAlign: "center", padding: "24px 0" }}>
                    <p style={{ marginBottom: 6 }}>Hali tahlil olinmagan.</p>
                    <p style={{ fontSize: 13 }}>Quyidagi <em>"Javob olish"</em> tugmasini bosing.</p>
                  </div>
                )}
              </Section>

              <div
                style={{
                  display: "flex",
                  gap: 10,
                  justifyContent: "flex-end",
                  marginTop: 12,
                  flexWrap: "wrap",
                }}
              >
                <button
                  className="btn-ghost hoverwhite"
                  onClick={() => navigate(`/mappage/${fanId}`)}
                  title="Mavzular ro'yxatiga qaytish"
                >
                  {t('mappage')}
                </button>
                <button onClick={() => setStep(1)}>Orqaga</button>
                <button
                  disabled={step2Loading || (step2Completed && !canRetakeStep2)}
                  onClick={sendComprehensiveAnswer}
                >
                  {step2Loading
                    ? "Yuborilmoqda..."
                    : canRetakeStep2 && step2AnswerCount !== 0
                    ? "Qayta javob olish"
                    : step2Completed && !canRetakeStep2
                    ? "Javob olindi"
                    : "Javob olish"}
                </button>
              </div>
            </>
          )}
        </>
      )}

      {helpOpen && (
        <div className="modal-overlay" onClick={closeHelpModal}>
          <div
            className="modal-window"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>{t("yordam")}</h3>
              <button
                className="modal-close"
                onClick={closeHelpModal}
                aria-label="Yopish"
              >
                ✕
              </button>
            </div>

            {helpLoading && (
              <div
                className="skeleton skeleton-box"
                style={{ height: 80, marginTop: 8 }}
              />
            )}

            {helpError && (
              <div className="error" style={{ marginTop: 8 }}>
                {helpError}
              </div>
            )}

            {!helpLoading && !helpError && (
              <div
                className="richtext modal-content"
                dangerouslySetInnerHTML={{ __html: helpHTML }}
              />
            )}

            <div className="modal-footer">
              <button onClick={closeHelpModal}>{t("yopish")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
